import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  CheckSquare, Plus, Users, FolderOpen, ArrowLeft, RefreshCw, Trash2, Edit,
  Calendar as CalendarIcon, Clock, MessageSquare, X, Crown, Tag, Bell,
  BarChart3, ChevronLeft, ChevronRight, Paperclip, Send, AlertTriangle,
  TrendingUp, UserPlus, FileText, Activity
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import useUser from "@/hooks/useUser";
import { PremiumLock, LoginRequired } from "@/components/PremiumLock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/tasks`;

export default function TaskManager() {
  const navigate = useNavigate();
  const { user, plan, isPremium, isEnterprise, isLoggedIn, loading: userLoading } = useUser();
  
  const [activeView, setActiveView] = useState("board");
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // New states for enhanced features
  const [teamMembers, setTeamMembers] = useState([]);
  const [tags, setTags] = useState([]);
  const [activities, setActivities] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState({ overdue: [], due_soon: [] });
  const [analytics, setAnalytics] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarTasks, setCalendarTasks] = useState([]);
  
  // Selected items
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Modal states
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  
  // Form states
  const [newClient, setNewClient] = useState({ name: "", company: "", email: "", phone: "" });
  const [newProject, setNewProject] = useState({ name: "", description: "", deadline: "" });
  const [newTask, setNewTask] = useState({ 
    title: "", description: "", priority: "medium", due_date: "", assigned_to: "", tags: []
  });
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "member" });
  const [newTag, setNewTag] = useState({ name: "", color: "#6366f1" });
  const [newComment, setNewComment] = useState("");

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!isLoggedIn || (!isPremium && !isEnterprise)) return;
    setLoading(true);
    try {
      const [clientsRes, projectsRes, tasksRes, statsRes, teamRes, tagsRes, activitiesRes, overdueRes] = await Promise.all([
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/`, { withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/team`, { withCredentials: true }),
        axios.get(`${API}/tags`, { withCredentials: true }),
        axios.get(`${API}/activity?limit=20`, { withCredentials: true }),
        axios.get(`${API}/overdue`, { withCredentials: true })
      ]);
      
      setClients(clientsRes.data.clients || []);
      setProjects(projectsRes.data.projects || []);
      setTasks(tasksRes.data.tasks || []);
      setStats(statsRes.data);
      setTeamMembers(teamRes.data.members || []);
      setTags(tagsRes.data.tags || []);
      setActivities(activitiesRes.data.activities || []);
      setOverdueTasks(overdueRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status !== 401) {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, isPremium, isEnterprise]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch analytics when view changes
  useEffect(() => {
    if (activeView === "analytics" && isLoggedIn) {
      axios.get(`${API}/analytics?days=30`, { withCredentials: true })
        .then(res => setAnalytics(res.data))
        .catch(console.error);
    }
  }, [activeView, isLoggedIn]);

  // Fetch calendar tasks
  useEffect(() => {
    if (activeView === "calendar" && isLoggedIn) {
      axios.get(`${API}/calendar?month=${calendarMonth}&year=${calendarYear}`, { withCredentials: true })
        .then(res => setCalendarTasks(res.data.tasks || []))
        .catch(console.error);
    }
  }, [activeView, calendarMonth, calendarYear, isLoggedIn]);

  // CRUD Handlers
  const handleAddClient = async () => {
    if (!newClient.name) return toast.error("Please enter client name");
    try {
      const response = await axios.post(`${API}/clients`, newClient, { withCredentials: true });
      setClients([...clients, response.data.client]);
      setNewClient({ name: "", company: "", email: "", phone: "" });
      setShowAddClient(false);
      toast.success("Client added!");
    } catch (error) { toast.error("Failed to add client"); }
  };

  const handleDeleteClient = async (clientId) => {
    if (!confirm("Delete this client and all associated projects/tasks?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`, { withCredentials: true });
      setClients(clients.filter(c => c.client_id !== clientId));
      setProjects(projects.filter(p => p.client_id !== clientId));
      toast.success("Client deleted");
    } catch (error) { toast.error("Failed to delete client"); }
  };

  const handleAddProject = async () => {
    if (!newProject.name || !selectedClient) return toast.error("Please select a client and enter project name");
    try {
      const response = await axios.post(`${API}/projects`, {
        ...newProject, client_id: selectedClient.client_id
      }, { withCredentials: true });
      setProjects([...projects, response.data.project]);
      setNewProject({ name: "", description: "", deadline: "" });
      setShowAddProject(false);
      toast.success("Project added!");
    } catch (error) { toast.error("Failed to add project"); }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Delete this project and all tasks?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      setProjects(projects.filter(p => p.project_id !== projectId));
      setTasks(tasks.filter(t => t.project_id !== projectId));
      toast.success("Project deleted");
    } catch (error) { toast.error("Failed to delete project"); }
  };

  const handleAddTask = async () => {
    if (!newTask.title || !selectedProject) return toast.error("Please select a project and enter task title");
    try {
      const response = await axios.post(`${API}/`, {
        ...newTask, project_id: selectedProject.project_id
      }, { withCredentials: true });
      setTasks([response.data.task, ...tasks]);
      setNewTask({ title: "", description: "", priority: "medium", due_date: "", assigned_to: "", tags: [] });
      setShowAddTask(false);
      toast.success("Task added!");
      fetchData();
    } catch (error) { toast.error("Failed to add task"); }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/${taskId}`, { status: newStatus }, { withCredentials: true });
      setTasks(tasks.map(t => t.task_id === taskId ? { ...t, status: newStatus } : t));
      toast.success("Task updated!");
    } catch (error) { toast.error("Failed to update task"); }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/${taskId}`, { withCredentials: true });
      setTasks(tasks.filter(t => t.task_id !== taskId));
      toast.success("Task deleted");
    } catch (error) { toast.error("Failed to delete task"); }
  };

  // Team Member handlers
  const handleAddMember = async () => {
    if (!newMember.name || !newMember.email) return toast.error("Please enter name and email");
    try {
      const response = await axios.post(`${API}/team`, newMember, { withCredentials: true });
      setTeamMembers([...teamMembers, response.data.member]);
      setNewMember({ name: "", email: "", role: "member" });
      setShowAddMember(false);
      toast.success("Team member added!");
    } catch (error) { toast.error("Failed to add member"); }
  };

  const handleDeleteMember = async (memberId) => {
    try {
      await axios.delete(`${API}/team/${memberId}`, { withCredentials: true });
      setTeamMembers(teamMembers.filter(m => m.member_id !== memberId));
      toast.success("Member removed");
    } catch (error) { toast.error("Failed to remove member"); }
  };

  // Tag handlers
  const handleAddTag = async () => {
    if (!newTag.name) return toast.error("Please enter tag name");
    try {
      const response = await axios.post(`${API}/tags`, newTag, { withCredentials: true });
      setTags([...tags, response.data.tag]);
      setNewTag({ name: "", color: "#6366f1" });
      setShowAddTag(false);
      toast.success("Tag created!");
    } catch (error) { toast.error("Failed to create tag"); }
  };

  // Comment handler
  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;
    try {
      const response = await axios.post(`${API}/${selectedTask.task_id}/comments`, {
        task_id: selectedTask.task_id, content: newComment
      }, { withCredentials: true });
      
      setSelectedTask({
        ...selectedTask,
        comments: [...(selectedTask.comments || []), response.data.comment]
      });
      setTasks(tasks.map(t => 
        t.task_id === selectedTask.task_id 
          ? { ...t, comments: [...(t.comments || []), response.data.comment] }
          : t
      ));
      setNewComment("");
      toast.success("Comment added!");
    } catch (error) { toast.error("Failed to add comment"); }
  };

  // Filter tasks
  const filteredTasks = selectedProject 
    ? tasks.filter(t => t.project_id === selectedProject.project_id)
    : tasks;
  const todoTasks = filteredTasks.filter(t => t.status === "todo");
  const inProgressTasks = filteredTasks.filter(t => t.status === "in_progress");
  const doneTasks = filteredTasks.filter(t => t.status === "done");

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-700 border-red-200";
      case "medium": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "low": return "bg-green-100 text-green-700 border-green-200";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getTagColor = (tagName) => {
    const tag = tags.find(t => t.name === tagName);
    return tag?.color || "#6366f1";
  };

  // Calendar helpers
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month - 1, 1).getDay();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  // Task Card Component
  const TaskCard = ({ task, showActions = true }) => (
    <div 
      className="bg-white p-3 sm:p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => { setSelectedTask(task); setShowTaskDetail(true); }}
      data-testid={`task-${task.task_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-gray-900 line-clamp-2 text-sm sm:text-base">{task.title}</h4>
        {showActions && (
          <Button 
            variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
            onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.task_id); }}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      {task.description && (
        <p className="text-xs sm:text-sm text-gray-500 mb-2 line-clamp-2">{task.description}</p>
      )}
      
      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {task.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: getTagColor(tag) }}>
              {tag}
            </span>
          ))}
        </div>
      )}
      
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Badge className={`text-xs ${getPriorityColor(task.priority)}`}>{task.priority}</Badge>
        
        <div className="flex items-center gap-2">
          {task.comments?.length > 0 && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> {task.comments.length}
            </span>
          )}
          {task.attachments?.length > 0 && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> {task.attachments.length}
            </span>
          )}
          {task.due_date && (
            <span className={`text-xs flex items-center gap-1 ${task.due_date < new Date().toISOString().split('T')[0] && task.status !== 'done' ? 'text-red-500' : 'text-gray-500'}`}>
              <CalendarIcon className="w-3 h-3" />
              {new Date(task.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
      </div>
      
      {task.assigned_to && (
        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
          <Users className="w-3 h-3" /> {task.assigned_to}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-button">
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-violet-700 to-purple-600 bg-clip-text text-transparent">
                MarkeeMix
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Notifications Badge */}
              {overdueTasks.overdue_count > 0 && (
                <Badge className="bg-red-100 text-red-700 hidden sm:flex">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {overdueTasks.overdue_count} Overdue
                </Badge>
              )}
              <Badge className="bg-violet-100 text-violet-700">
                <Crown className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Premium Check - Show for non-logged in OR non-premium users */}
        {(!isLoggedIn || (!isPremium && !isEnterprise)) && !userLoading && (
          <PremiumLock feature="Task Manager" description="Manage your marketing agency clients, projects, and tasks. Login with a Pro or Enterprise plan to access this feature." />
        )}

        {/* Main Content */}
        {isLoggedIn && (isPremium || isEnterprise) && (
          <>
            {/* Stats Overview */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <Card className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.clients}</p>
                      <p className="text-xs sm:text-sm text-gray-600">Clients</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-pink-50">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <FolderOpen className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.projects}</p>
                      <p className="text-xs sm:text-sm text-gray-600">Projects</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-4 bg-gradient-to-br from-orange-50 to-amber-50">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.tasks?.in_progress || 0}</p>
                      <p className="text-xs sm:text-sm text-gray-600">In Progress</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <CheckSquare className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.tasks?.done || 0}</p>
                      <p className="text-xs sm:text-sm text-gray-600">Completed</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Tabs Navigation */}
            <Tabs value={activeView} onValueChange={setActiveView} className="mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full sm:w-auto">
                  <TabsTrigger value="board" data-testid="view-board">
                    <CheckSquare className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Board</span>
                  </TabsTrigger>
                  <TabsTrigger value="calendar" data-testid="view-calendar">
                    <CalendarIcon className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Calendar</span>
                  </TabsTrigger>
                  <TabsTrigger value="team" data-testid="view-team">
                    <Users className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Team</span>
                  </TabsTrigger>
                  <TabsTrigger value="clients" data-testid="view-clients">
                    <FolderOpen className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Clients</span>
                  </TabsTrigger>
                  <TabsTrigger value="analytics" data-testid="view-analytics">
                    <BarChart3 className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Analytics</span>
                  </TabsTrigger>
                  <TabsTrigger value="activity" data-testid="view-activity">
                    <Activity className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Activity</span>
                  </TabsTrigger>
                </TabsList>
                
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => setShowAddClient(true)} variant="outline" size="sm" data-testid="add-client-btn">
                    <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Client</span>
                  </Button>
                  <Button onClick={() => setShowAddProject(true)} variant="outline" size="sm" data-testid="add-project-btn">
                    <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Project</span>
                  </Button>
                  <Button onClick={() => setShowAddTask(true)} className="bg-violet-600 hover:bg-violet-700" size="sm" data-testid="add-task-btn">
                    <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Task</span>
                  </Button>
                </div>
              </div>

              {/* Project Selector & Tags (for Board view) */}
              {activeView === "board" && (
                <div className="flex flex-wrap gap-3 mb-4">
                  <Select 
                    value={selectedProject?.project_id || "all"} 
                    onValueChange={(v) => setSelectedProject(v === "all" ? null : projects.find(p => p.project_id === v))}
                  >
                    <SelectTrigger className="w-48" data-testid="project-selector">
                      <SelectValue placeholder="All Projects" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Projects</SelectItem>
                      {projects.map(project => (
                        <SelectItem key={project.project_id} value={project.project_id}>{project.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button variant="outline" size="sm" onClick={() => setShowAddTag(true)}>
                    <Tag className="w-4 h-4 mr-1" /> Manage Tags
                  </Button>
                </div>
              )}

              {/* ==================== KANBAN BOARD ==================== */}
              <TabsContent value="board">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                  {/* To Do Column */}
                  <div className="bg-gray-100 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                        To Do <Badge variant="secondary">{todoTasks.length}</Badge>
                      </h3>
                    </div>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {todoTasks.map(task => (
                        <div key={task.task_id}>
                          <TaskCard task={task} />
                          <Button size="sm" variant="outline" className="w-full mt-2 text-xs"
                            onClick={(e) => { e.stopPropagation(); handleUpdateTaskStatus(task.task_id, "in_progress"); }}>
                            Start Task
                          </Button>
                        </div>
                      ))}
                      {todoTasks.length === 0 && <p className="text-center text-gray-400 py-8">No tasks</p>}
                    </div>
                  </div>

                  {/* In Progress Column */}
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-blue-700 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        In Progress <Badge className="bg-blue-100 text-blue-700">{inProgressTasks.length}</Badge>
                      </h3>
                    </div>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {inProgressTasks.map(task => (
                        <div key={task.task_id}>
                          <TaskCard task={task} />
                          <div className="mt-2 flex gap-1">
                            <Button size="sm" variant="outline" className="flex-1 text-xs"
                              onClick={(e) => { e.stopPropagation(); handleUpdateTaskStatus(task.task_id, "todo"); }}>
                              Back
                            </Button>
                            <Button size="sm" className="flex-1 text-xs bg-green-600 hover:bg-green-700"
                              onClick={(e) => { e.stopPropagation(); handleUpdateTaskStatus(task.task_id, "done"); }}>
                              Done
                            </Button>
                          </div>
                        </div>
                      ))}
                      {inProgressTasks.length === 0 && <p className="text-center text-gray-400 py-8">No tasks</p>}
                    </div>
                  </div>

                  {/* Done Column */}
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-green-700 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Done <Badge className="bg-green-100 text-green-700">{doneTasks.length}</Badge>
                      </h3>
                    </div>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {doneTasks.map(task => <TaskCard key={task.task_id} task={task} />)}
                      {doneTasks.length === 0 && <p className="text-center text-gray-400 py-8">No completed tasks</p>}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ==================== CALENDAR VIEW ==================== */}
              <TabsContent value="calendar">
                <Card className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <Button variant="outline" onClick={() => {
                      if (calendarMonth === 1) { setCalendarMonth(12); setCalendarYear(calendarYear - 1); }
                      else setCalendarMonth(calendarMonth - 1);
                    }}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="text-lg sm:text-xl font-semibold">{monthNames[calendarMonth - 1]} {calendarYear}</h3>
                    <Button variant="outline" onClick={() => {
                      if (calendarMonth === 12) { setCalendarMonth(1); setCalendarYear(calendarYear + 1); }
                      else setCalendarMonth(calendarMonth + 1);
                    }}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 sm:gap-2">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                      <div key={day} className="text-center text-xs sm:text-sm font-medium text-gray-500 py-2">{day}</div>
                    ))}
                    
                    {/* Empty cells for days before month starts */}
                    {[...Array(getFirstDayOfMonth(calendarMonth, calendarYear))].map((_, i) => (
                      <div key={`empty-${i}`} className="h-16 sm:h-24"></div>
                    ))}
                    
                    {/* Days of month */}
                    {[...Array(getDaysInMonth(calendarMonth, calendarYear))].map((_, i) => {
                      const day = i + 1;
                      const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const dayTasks = calendarTasks.filter(t => t.due_date?.startsWith(dateStr));
                      const isToday = new Date().toISOString().split('T')[0] === dateStr;
                      
                      return (
                        <div key={day} className={`h-16 sm:h-24 border rounded-lg p-1 ${isToday ? 'border-violet-500 bg-violet-50' : 'border-gray-200'}`}>
                          <div className={`text-xs sm:text-sm font-medium ${isToday ? 'text-violet-700' : 'text-gray-700'}`}>{day}</div>
                          <div className="mt-1 space-y-0.5 overflow-hidden">
                            {dayTasks.slice(0, 2).map(task => (
                              <div key={task.task_id} className={`text-xs truncate px-1 rounded ${
                                task.status === 'done' ? 'bg-green-100 text-green-700' :
                                task.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {task.title}
                              </div>
                            ))}
                            {dayTasks.length > 2 && (
                              <div className="text-xs text-gray-400">+{dayTasks.length - 2} more</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </TabsContent>

              {/* ==================== TEAM VIEW ==================== */}
              <TabsContent value="team">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Team Members</h3>
                  <Button onClick={() => setShowAddMember(true)} data-testid="add-member-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Add Member
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teamMembers.map(member => (
                    <Card key={member.member_id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <img src={member.avatar} alt={member.name} className="w-12 h-12 rounded-full" />
                          <div>
                            <h4 className="font-semibold">{member.name}</h4>
                            <p className="text-sm text-gray-500">{member.email}</p>
                            <Badge variant="outline" className="mt-1">{member.role}</Badge>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMember(member.member_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="mt-4 flex gap-4 text-sm text-gray-600">
                        <span>Assigned: {member.tasks_assigned}</span>
                        <span>Completed: {member.tasks_completed}</span>
                      </div>
                    </Card>
                  ))}
                  
                  {teamMembers.length === 0 && (
                    <Card className="p-8 text-center col-span-full">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <h3 className="font-semibold text-gray-600 mb-2">No Team Members</h3>
                      <p className="text-gray-400 mb-4">Add team members to assign tasks</p>
                      <Button onClick={() => setShowAddMember(true)}>
                        <UserPlus className="w-4 h-4 mr-2" /> Add Member
                      </Button>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* ==================== CLIENTS VIEW ==================== */}
              <TabsContent value="clients">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {clients.map(client => (
                    <Card key={client.client_id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">{client.name}</h3>
                          {client.company && <p className="text-sm text-gray-500">{client.company}</p>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteClient(client.client_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {client.email && <p className="text-sm text-gray-600 mb-1">{client.email}</p>}
                      {client.phone && <p className="text-sm text-gray-600 mb-3">{client.phone}</p>}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <Badge variant="outline">{client.projects_count || 0} Projects</Badge>
                        <Button size="sm" variant="outline" onClick={() => {
                          setSelectedClient(client);
                          setShowAddProject(true);
                        }}>
                          <Plus className="w-3 h-3 mr-1" /> Project
                        </Button>
                      </div>
                    </Card>
                  ))}
                  
                  {clients.length === 0 && (
                    <Card className="p-8 text-center col-span-full">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <h3 className="font-semibold text-gray-600 mb-2">No Clients Yet</h3>
                      <p className="text-gray-400 mb-4">Add your first client to get started</p>
                      <Button onClick={() => setShowAddClient(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add Client
                      </Button>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* ==================== ANALYTICS VIEW ==================== */}
              <TabsContent value="analytics">
                {analytics ? (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="p-4 text-center">
                        <TrendingUp className="w-8 h-8 text-violet-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-violet-600">{analytics.completion_rate}%</p>
                        <p className="text-sm text-gray-500">Completion Rate</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <CheckSquare className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-600">{analytics.total_tasks}</p>
                        <p className="text-sm text-gray-500">Total Tasks</p>
                      </Card>
                      <Card className="p-4 text-center bg-red-50">
                        <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-red-600">{overdueTasks.overdue_count || 0}</p>
                        <p className="text-sm text-gray-500">Overdue Tasks</p>
                      </Card>
                      <Card className="p-4 text-center bg-yellow-50">
                        <Bell className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-yellow-600">{overdueTasks.due_soon_count || 0}</p>
                        <p className="text-sm text-gray-500">Due Soon</p>
                      </Card>
                    </div>

                    {/* Project Progress */}
                    <Card className="p-4 sm:p-6">
                      <h3 className="text-lg font-semibold mb-4">Project Progress</h3>
                      <div className="space-y-4">
                        {analytics.project_progress?.map(proj => (
                          <div key={proj.project_id}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{proj.name}</span>
                              <span className="text-gray-500">{proj.completed_tasks}/{proj.total_tasks} ({proj.progress_percent}%)</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-violet-600 rounded-full transition-all" style={{ width: `${proj.progress_percent}%` }}></div>
                            </div>
                          </div>
                        ))}
                        {!analytics.project_progress?.length && (
                          <p className="text-gray-400 text-center py-4">No projects to show</p>
                        )}
                      </div>
                    </Card>

                    {/* Team Productivity */}
                    <Card className="p-4 sm:p-6">
                      <h3 className="text-lg font-semibold mb-4">Team Productivity</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(analytics.team_productivity || {}).map(([name, data]) => (
                          <div key={name} className="p-3 bg-gray-50 rounded-lg">
                            <h4 className="font-medium mb-2">{name}</h4>
                            <div className="flex gap-4 text-sm">
                              <span className="text-blue-600">Total: {data.total}</span>
                              <span className="text-green-600">Done: {data.completed}</span>
                              <span className="text-orange-600">Active: {data.in_progress}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                ) : (
                  <Card className="p-8 text-center">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Loading analytics...</p>
                  </Card>
                )}
              </TabsContent>

              {/* ==================== ACTIVITY LOG ==================== */}
              <TabsContent value="activity">
                <Card className="p-4 sm:p-6">
                  <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {activities.map(activity => (
                      <div key={activity.activity_id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          activity.action === 'created' ? 'bg-green-100 text-green-600' :
                          activity.action === 'completed' ? 'bg-blue-100 text-blue-600' :
                          activity.action === 'deleted' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {activity.action === 'created' && <Plus className="w-4 h-4" />}
                          {activity.action === 'completed' && <CheckSquare className="w-4 h-4" />}
                          {activity.action === 'deleted' && <Trash2 className="w-4 h-4" />}
                          {activity.action === 'commented' && <MessageSquare className="w-4 h-4" />}
                        </div>
                        <div>
                          <p className="text-sm">
                            <span className="font-medium capitalize">{activity.action}</span> {activity.entity_type}
                            {activity.details?.title && <span className="text-gray-600"> "{activity.details.title}"</span>}
                          </p>
                          <p className="text-xs text-gray-400">{new Date(activity.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                    {activities.length === 0 && (
                      <p className="text-center text-gray-400 py-8">No recent activity</p>
                    )}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>

            {/* ==================== MODALS ==================== */}
            
            {/* Add Client Modal */}
            {showAddClient && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Add New Client</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddClient(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-4">
                    <Input placeholder="Client Name *" value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="client-name-input" />
                    <Input placeholder="Company" value={newClient.company}
                      onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} />
                    <Input placeholder="Email" type="email" value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
                    <Input placeholder="Phone" value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowAddClient(false)} className="flex-1">Cancel</Button>
                      <Button onClick={handleAddClient} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="save-client-btn">Add Client</Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Add Project Modal */}
            {showAddProject && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Add New Project</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddProject(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-4">
                    <Select value={selectedClient?.client_id || ""} onValueChange={(v) => setSelectedClient(clients.find(c => c.client_id === v))}>
                      <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Select Client *" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(client => <SelectItem key={client.client_id} value={client.client_id}>{client.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Project Name *" value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} data-testid="project-name-input" />
                    <Textarea placeholder="Description" value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} rows={3} />
                    <Input type="date" placeholder="Deadline" value={newProject.deadline}
                      onChange={(e) => setNewProject({ ...newProject, deadline: e.target.value })} />
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowAddProject(false)} className="flex-1">Cancel</Button>
                      <Button onClick={handleAddProject} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="save-project-btn">Add Project</Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Add Task Modal */}
            {showAddTask && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Add New Task</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddTask(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-4">
                    <Select value={selectedProject?.project_id || ""} onValueChange={(v) => setSelectedProject(projects.find(p => p.project_id === v))}>
                      <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Select Project *" /></SelectTrigger>
                      <SelectContent>
                        {projects.map(project => <SelectItem key={project.project_id} value={project.project_id}>{project.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Task Title *" value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} data-testid="task-title-input" />
                    <Textarea placeholder="Description" value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} rows={3} />
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                        <SelectTrigger data-testid="task-priority-select"><SelectValue placeholder="Priority" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
                    </div>
                    <Select value={newTask.assigned_to || ""} onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v })}>
                      <SelectTrigger><SelectValue placeholder="Assign To (Optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Unassigned</SelectItem>
                        {teamMembers.map(m => <SelectItem key={m.member_id} value={m.name}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {/* Tags Selection */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Tags</label>
                      <div className="flex flex-wrap gap-2">
                        {tags.map(tag => (
                          <button key={tag.tag_id} type="button" onClick={() => {
                            const current = newTask.tags || [];
                            setNewTask({
                              ...newTask,
                              tags: current.includes(tag.name) ? current.filter(t => t !== tag.name) : [...current, tag.name]
                            });
                          }}
                            className={`text-xs px-3 py-1 rounded-full border transition-all ${
                              newTask.tags?.includes(tag.name) ? 'text-white' : 'text-gray-700'
                            }`}
                            style={{ backgroundColor: newTask.tags?.includes(tag.name) ? tag.color : 'transparent', borderColor: tag.color }}>
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowAddTask(false)} className="flex-1">Cancel</Button>
                      <Button onClick={handleAddTask} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="save-task-btn">Add Task</Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Add Team Member Modal */}
            {showAddMember && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Add Team Member</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddMember(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-4">
                    <Input placeholder="Name *" value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} data-testid="member-name-input" />
                    <Input placeholder="Email *" type="email" value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} data-testid="member-email-input" />
                    <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                      <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowAddMember(false)} className="flex-1">Cancel</Button>
                      <Button onClick={handleAddMember} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="save-member-btn">Add Member</Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Add Tag Modal */}
            {showAddTag && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Manage Tags</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddTag(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tags.map(tag => (
                        <span key={tag.tag_id} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full text-white"
                          style={{ backgroundColor: tag.color }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="New tag name" value={newTag.name}
                        onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} />
                      <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer" />
                    </div>
                    <Button onClick={handleAddTag} className="w-full bg-violet-600 hover:bg-violet-700">Create Tag</Button>
                  </div>
                </Card>
              </div>
            )}

            {/* Task Detail Modal */}
            {showTaskDetail && selectedTask && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold">{selectedTask.title}</h3>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={getPriorityColor(selectedTask.priority)}>{selectedTask.priority}</Badge>
                        <Badge variant="outline">{selectedTask.status}</Badge>
                        {selectedTask.due_date && (
                          <span className={`text-sm ${selectedTask.due_date < new Date().toISOString().split('T')[0] && selectedTask.status !== 'done' ? 'text-red-500' : 'text-gray-500'}`}>
                            Due: {new Date(selectedTask.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowTaskDetail(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  
                  {selectedTask.description && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Description</h4>
                      <p className="text-gray-600">{selectedTask.description}</p>
                    </div>
                  )}
                  
                  {selectedTask.tags?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedTask.tags.map((tag, idx) => (
                          <span key={idx} className="text-xs px-3 py-1 rounded-full text-white" style={{ backgroundColor: getTagColor(tag) }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Comments Section */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Comments ({selectedTask.comments?.length || 0})
                    </h4>
                    <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                      {selectedTask.comments?.map(comment => (
                        <div key={comment.comment_id} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium">{comment.author}</span>
                            <span className="text-gray-400">{new Date(comment.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-gray-700">{comment.content}</p>
                        </div>
                      ))}
                      {!selectedTask.comments?.length && (
                        <p className="text-gray-400 text-center py-4">No comments yet</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="Add a comment..." value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddComment()} />
                      <Button onClick={handleAddComment}><Send className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="border-t pt-4 mt-4 flex gap-2 flex-wrap">
                    {selectedTask.status !== 'done' && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700"
                        onClick={() => { handleUpdateTaskStatus(selectedTask.task_id, 'done'); setShowTaskDetail(false); }}>
                        <CheckSquare className="w-4 h-4 mr-1" /> Mark Complete
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { handleDeleteTask(selectedTask.task_id); setShowTaskDetail(false); }}
                      className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Task
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
